/**
 * LocalizationContext (KQA-12 / KQA-13).
 *
 * Single source of truth for how dates, numbers, and currency render across the
 * app. Previously the project form hardcoded mm/dd/yyyy + USD and the
 * LocalizationSection only *displayed* mock settings. This context loads the
 * user's persisted preferences (date_format, time_format, number_format,
 * currency, timezone) and exposes formatter helpers that honor them.
 *
 * KQA-13 reuses `timezone` here so dates render in the user's tz from one place.
 * Defaults are safe for unauthenticated/test contexts (no crash, US fallback).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react'
import { localizationService, LocalizationPrefs } from '../services/localization'
import { useAuth } from './AuthContext'

const DEFAULTS: LocalizationPrefs = {
  date_format: 'MM/DD/YYYY',
  time_format: '12h',
  number_format: '1,234.56',
  currency: 'USD',
  timezone: 'UTC',
}

interface LocalizationContextType {
  prefs: LocalizationPrefs
  loading: boolean
  refresh: () => Promise<void>
  update: (patch: Partial<LocalizationPrefs>) => Promise<void>
  formatDate: (
    value: string | Date | null | undefined,
    opts?: { withTime?: boolean; withTimeZone?: boolean }
  ) => string
  /** The user's configured IANA display timezone (KQA-13). */
  timezone: string
  formatNumber: (value: number | string | null | undefined, fractionDigits?: number) => string
  formatCurrency: (value: number | string | null | undefined, currencyOverride?: string) => string
}

const LocalizationContext = createContext<LocalizationContextType | undefined>(undefined)

// Maps our stored date_format token to an Intl-friendly locale that produces
// the matching order. We keep the explicit token so we never guess.
const LOCALE_FOR_DATE: Record<LocalizationPrefs['date_format'], string> = {
  'MM/DD/YYYY': 'en-US',
  'DD/MM/YYYY': 'en-GB',
  'YYYY-MM-DD': 'en-CA', // ISO-style
}

const LOCALE_FOR_NUMBER: Record<LocalizationPrefs['number_format'], string> = {
  '1,234.56': 'en-US',
  '1.234,56': 'de-DE',
  '1 234,56': 'fr-FR',
}

export function LocalizationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [prefs, setPrefs] = useState<LocalizationPrefs>(DEFAULTS)
  const [loading, setLoading] = useState(false)

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const data = await localizationService.get()
      setPrefs(data)
    } catch {
      setPrefs(DEFAULTS)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (user) {
      refresh()
    } else {
      setPrefs(DEFAULTS)
    }
  }, [user, refresh])

  const update = useCallback(async (patch: Partial<LocalizationPrefs>) => {
    const data = await localizationService.update(patch)
    setPrefs(data)
  }, [])

  const formatDate = useCallback(
    (value: string | Date | null | undefined, opts?: { withTime?: boolean; withTimeZone?: boolean }) => {
      if (value == null || value === '') return '—'
      const d = typeof value === 'string' ? new Date(value) : value
      if (isNaN(d.getTime())) return '—'
      const locale = LOCALE_FOR_DATE[prefs.date_format] || 'en-US'
      const fmtOpts: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        timeZone: prefs.timezone || 'UTC',
      }
      if (opts?.withTime) {
        fmtOpts.hour = '2-digit'
        fmtOpts.minute = '2-digit'
        fmtOpts.hour12 = prefs.time_format === '12h'
      }
      // KQA-13: surface the tz abbreviation (e.g. "EST") so dates rendered on
      // shared/cross-tz views are unambiguous.
      if (opts?.withTimeZone) {
        fmtOpts.timeZoneName = 'short'
      }
      try {
        return new Intl.DateTimeFormat(locale, fmtOpts).format(d)
      } catch {
        return d.toISOString().slice(0, 10)
      }
    },
    [prefs.date_format, prefs.timezone, prefs.time_format]
  )

  const formatNumber = useCallback(
    (value: number | string | null | undefined, fractionDigits?: number) => {
      if (value == null || value === '') return '—'
      const n = typeof value === 'string' ? parseFloat(value) : value
      if (isNaN(n)) return '—'
      const locale = LOCALE_FOR_NUMBER[prefs.number_format] || 'en-US'
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits ?? 2,
      }).format(n)
    },
    [prefs.number_format]
  )

  const formatCurrency = useCallback(
    (value: number | string | null | undefined, currencyOverride?: string) => {
      if (value == null || value === '') return '—'
      const n = typeof value === 'string' ? parseFloat(value) : value
      if (isNaN(n)) return '—'
      const locale = LOCALE_FOR_NUMBER[prefs.number_format] || 'en-US'
      const currency = (currencyOverride || prefs.currency || 'USD').toUpperCase()
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(n)
      } catch {
        return `${currency} ${formatNumber(n, 2)}`
      }
    },
    [prefs.number_format, prefs.currency, formatNumber]
  )

  const value = useMemo(
    () => ({ prefs, loading, refresh, update, formatDate, formatNumber, formatCurrency, timezone: prefs.timezone || 'UTC' }),
    [prefs, loading, refresh, update, formatDate, formatNumber, formatCurrency]
  )

  return <LocalizationContext.Provider value={value}>{children}</LocalizationContext.Provider>
}

export function useLocalization(): LocalizationContextType {
  const ctx = useContext(LocalizationContext)
  if (!ctx) {
    // Safe identity-ish fallback for components rendered outside the provider
    // (e.g. isolated unit tests).
    const fallback = {
      prefs: DEFAULTS,
      loading: false,
      timezone: DEFAULTS.timezone,
      refresh: async () => {},
      update: async () => {},
      formatDate: (v: string | Date | null | undefined) =>
        v == null || v === '' ? '—' : new Date(v).toLocaleDateString('en-US'),
      formatNumber: (v: number | string | null | undefined, f?: number) =>
        v == null || v === '' ? '—' : Number(v).toLocaleString('en-US', { maximumFractionDigits: f ?? 2 }),
      formatCurrency: (v: number | string | null | undefined, c?: string) =>
        v == null || v === '' ? '—' : `${(c || 'USD').toUpperCase()} ${Number(v).toFixed(2)}`,
    }
    return fallback as LocalizationContextType
  }
  return ctx
}
